import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { activeMembership, useSession } from '../../lib/store';
import { Card, Notice, PageHeading, Spinner } from '../../components/Shell';
import { TextArea, TextField, Toggle } from '../../components/Field';
import { Stamp } from '../../components/Stamp';
import { longDate } from '../../lib/format';
import type { AgreementSummary, ClientProfile } from '../../lib/types';

/**
 * The client half of the intake pack: contact details, emergency contact, vet,
 * and how the sitter gets into the home. Signed agreements are listed here too,
 * because this is the page a person looks at when they want their paperwork.
 */
export function MyDetails() {
  const session = useSession();
  const membership = activeMembership(session);
  const tenantId = session.tenant?.tenantId;
  const clientId = membership?.clientId;

  const [client, setClient] = useState<ClientProfile | null>(null);
  const [agreements, setAgreements] = useState<AgreementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!tenantId || !clientId) return;
    void Promise.all([
      api<{ client: ClientProfile }>(`/tenants/${tenantId}/clients/${clientId}`),
      api<{ agreements: AgreementSummary[] }>(`/tenants/${tenantId}/agreements`),
    ])
      .then(([clientResult, agreementResult]) => {
        setClient(clientResult.client);
        setAgreements(agreementResult.agreements);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Your details could not be loaded.'))
      .finally(() => setLoading(false));
  }, [tenantId, clientId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Spinner label="Opening your details" />
      </div>
    );
  }

  if (!client || !tenantId || !clientId) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Notice tone="error">{error ?? 'Your client record is not available.'}</Notice>
      </div>
    );
  }

  const patch = <K extends keyof ClientProfile>(key: K, value: ClientProfile[K]) =>
    setClient((prev) => (prev ? { ...prev, [key]: value } : prev));

  const patchGroup = <K extends 'emergencyContact' | 'vet' | 'homeAccess'>(
    group: K,
    value: Partial<NonNullable<ClientProfile[K]>>
  ) =>
    setClient((prev) =>
      prev ? ({ ...prev, [group]: { ...(prev[group] ?? {}), ...value } } as ClientProfile) : prev
    );

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    setFieldErrors({});

    void api<{ client: ClientProfile }>(`/tenants/${tenantId}/clients/${clientId}`, {
      method: 'PATCH',
      body: {
        fullName: client.fullName,
        mobile: client.mobile,
        workNumber: client.workNumber,
        address: client.address,
        emergencyContact: client.emergencyContact,
        vet: client.vet,
        homeAccess: client.homeAccess,
        smsConsent: Boolean(client.messaging?.smsConsent && !client.messaging.optedOutAt),
      },
    })
      .then((result) => {
        setClient(result.client);
        setSaved(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          setFieldErrors(err.fieldErrors ?? {});
          setError(err.fieldErrors ? 'Some fields need another look.' : err.message);
        } else {
          setError('Your details did not save. Try again.');
        }
      })
      .finally(() => setSaving(false));
  };

  const downloadAgreement = (agreementId: string) => {
    void api<{ downloadUrl: string }>(`/tenants/${tenantId}/agreements/${agreementId}/pdf`)
      .then((result) => window.open(result.downloadUrl, '_blank', 'noopener'))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The copy could not be opened.'));
  };

  const home = client.homeAccess;

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-12">
      <PageHeading eyebrow="Client file" title="My details" />

      {saved && (
        <div className="mb-8">
          <Notice tone="ok">Your details are saved.</Notice>
        </div>
      )}
      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-8">
        <Card tab="Contact" className="p-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField
              label="Full name"
              value={client.fullName}
              onChange={(value) => patch('fullName', value)}
              error={fieldErrors.fullName}
              required
            />
            <TextField label="Email" value={client.email} onChange={() => undefined} disabled />
            <TextField
              label="Mobile"
              type="tel"
              value={client.mobile}
              onChange={(value) => patch('mobile', value)}
              error={fieldErrors.mobile}
              required
            />
            <TextField
              label="Work number"
              type="tel"
              value={client.workNumber ?? ''}
              onChange={(value) => patch('workNumber', value)}
              error={fieldErrors.workNumber}
            />
          </div>
          <div className="mt-6">
            <TextArea
              label="Home address"
              value={client.address ?? ''}
              onChange={(value) => patch('address', value)}
              rows={2}
              error={fieldErrors.address}
            />
          </div>

          {/* Opt-in, never opt-out, and asked for plainly. What follows the
              yes is a text about somebody's own animal, so the hint says what
              they will actually get rather than the word "communications". */}
          <div className="mt-6 border-t border-[color:var(--color-rule)] pt-6">
            <Toggle
              label="Text me about my pets"
              checked={Boolean(client.messaging?.smsConsent && !client.messaging.optedOutAt)}
              onChange={(checked) =>
                setClient((current) =>
                  current
                    ? {
                        ...current,
                        messaging: checked
                          ? { ...current.messaging, smsConsent: true, optedOutAt: undefined }
                          : { ...current.messaging, smsConsent: false },
                      }
                    : current
                )
              }
              hint="Photos and updates while they are in our care, and a note a couple of days before a stay. Reply STOP to any of them to switch this off."
            />
          </div>
        </Card>

        <Card tab="Emergency contact" tint="yellow" className="p-6">
          <p className="mb-5 text-sm text-[color:var(--color-ink-soft)]">
            Someone who is not travelling with you, and who could collect your pet if we cannot reach
            you.
          </p>
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField
              label="Name"
              value={client.emergencyContact?.name ?? ''}
              onChange={(value) => patchGroup('emergencyContact', { name: value })}
              error={fieldErrors['emergencyContact.name']}
            />
            <TextField
              label="Relationship to you"
              value={client.emergencyContact?.relationship ?? ''}
              onChange={(value) => patchGroup('emergencyContact', { relationship: value })}
              error={fieldErrors['emergencyContact.relationship']}
            />
            <TextField
              label="Mobile"
              type="tel"
              value={client.emergencyContact?.mobile ?? ''}
              onChange={(value) => patchGroup('emergencyContact', { mobile: value })}
              error={fieldErrors['emergencyContact.mobile']}
            />
            <TextField
              label="Email"
              type="email"
              value={client.emergencyContact?.email ?? ''}
              onChange={(value) => patchGroup('emergencyContact', { email: value })}
              error={fieldErrors['emergencyContact.email']}
            />
          </div>
          <div className="mt-4">
            <Toggle
              label="They have a key to my home"
              checked={client.emergencyContact?.hasKey ?? false}
              onChange={(value) => patchGroup('emergencyContact', { hasKey: value })}
            />
          </div>
        </Card>

        <Card tab="Veterinarian" tint="blue" className="p-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField
              label="Practice name"
              value={client.vet?.name ?? ''}
              onChange={(value) => patchGroup('vet', { name: value })}
              error={fieldErrors['vet.name']}
            />
            <TextField
              label="Phone"
              type="tel"
              value={client.vet?.phone ?? ''}
              onChange={(value) => patchGroup('vet', { phone: value })}
              error={fieldErrors['vet.phone']}
            />
            <TextField
              label="Address"
              value={client.vet?.address ?? ''}
              onChange={(value) => patchGroup('vet', { address: value })}
            />
            <TextField
              label="Opening hours"
              value={client.vet?.openingHours ?? ''}
              onChange={(value) => patchGroup('vet', { openingHours: value })}
            />
          </div>
          <p className="mt-5 text-sm text-[color:var(--color-ink-soft)]">
            Tell your vet you will be away and leave a payment method with them, so no cost falls to
            your sitter.
          </p>
        </Card>

        <Card tab="Access to my home" tint="pink" className="p-6">
          <p className="mb-5 text-sm text-[color:var(--color-ink-soft)]">
            Only needed for in-home visits and house sitting.
          </p>

          <Toggle
            label="I will provide a key"
            checked={home?.providingKey ?? false}
            onChange={(value) => patchGroup('homeAccess', { providingKey: value })}
          />

          {!home?.providingKey && (
            <div className="mt-4">
              <TextArea
                label="How to get in"
                value={home?.entryInstructions ?? ''}
                onChange={(value) => patchGroup('homeAccess', { entryInstructions: value })}
                rows={2}
                error={fieldErrors['homeAccess.entryInstructions']}
                placeholder="Lockbox on the gas meter, code 4821. Side gate is unlatched."
              />
            </div>
          )}

          <div className="mt-5 grid gap-6 sm:grid-cols-2">
            <TextField
              label="Which door"
              value={home?.entryDoor ?? ''}
              onChange={(value) => patchGroup('homeAccess', { entryDoor: value })}
            />
            <TextField
              label="Areas that are off limits"
              value={home?.restrictedAreas ?? ''}
              onChange={(value) => patchGroup('homeAccess', { restrictedAreas: value })}
            />
          </div>

          <div className="mt-5">
            <Toggle
              label="The alarm will be set"
              checked={home?.alarmPresent ?? false}
              onChange={(value) => patchGroup('homeAccess', { alarmPresent: value })}
            />
            {home?.alarmPresent && (
              <div className="mt-3">
                <TextField
                  label="Alarm code"
                  value={home?.alarmCode ?? ''}
                  onChange={(value) => patchGroup('homeAccess', { alarmCode: value })}
                  error={fieldErrors['homeAccess.alarmCode']}
                  hint="Stored encrypted and shown only to your sitter."
                />
              </div>
            )}
          </div>

          <div className="mt-5">
            <Toggle
              label="There are cameras inside or outside"
              checked={home?.camerasPresent ?? false}
              onChange={(value) => patchGroup('homeAccess', { camerasPresent: value })}
              hint="Disclosing these is a term of the services agreement."
            />
            {home?.camerasPresent && (
              <div className="mt-3">
                <TextArea
                  label="Where they are"
                  value={home?.cameraLocations ?? ''}
                  onChange={(value) => patchGroup('homeAccess', { cameraLocations: value })}
                  rows={2}
                  error={fieldErrors['homeAccess.cameraLocations']}
                />
              </div>
            )}
          </div>
        </Card>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving' : 'Save my details'}
        </button>
      </form>

      <section className="mt-14">
        <h2 className="mb-5 text-2xl">My agreements</h2>
        {agreements.length === 0 ? (
          <p className="text-sm text-[color:var(--color-ink-soft)]">
            Nothing signed yet. You will be sent a link when your first booking needs one.
          </p>
        ) : (
          <ul className="space-y-2">
            {agreements.map((agreement) => (
              <li
                key={agreement.agreementId}
                className="card flex flex-wrap items-center justify-between gap-4 p-4"
              >
                <div>
                  <p className="font-semibold">{agreement.season} services agreement</p>
                  <p className="typed text-xs text-[color:var(--color-ink-soft)]">
                    {agreement.signedAt
                      ? `Signed ${longDate(agreement.signedAt)}`
                      : `Sent ${longDate(agreement.sentAt)}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {agreement.status === 'signed' ? (
                    <>
                      <Stamp tone="ok" flat>
                        Signed
                      </Stamp>
                      <button
                        type="button"
                        className="btn btn-quiet"
                        onClick={() => downloadAgreement(agreement.agreementId)}
                      >
                        Download PDF
                      </button>
                    </>
                  ) : (
                    <Stamp tone="quiet" flat>
                      {agreement.status === 'viewed' ? 'Opened, not signed' : 'Waiting'}
                    </Stamp>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
